/**
 * Minimal structural shape of a react-querybuilder rule/group, so this helper can be shared by
 * every conditions editor in the app regardless of how strongly each one types its own rules.
 */
interface QueryBuilderRuleLike {
  field?: unknown;
  value?: unknown;
}

interface QueryBuilderGroupLike {
  rules?: readonly (QueryBuilderRuleLike | QueryBuilderGroupLike)[];
}

const isGroup = (
  rule: QueryBuilderRuleLike | QueryBuilderGroupLike,
): rule is QueryBuilderGroupLike => 'rules' in rule;

/**
 * A rule is complete once the user has picked a field and entered a value.
 *
 * `field` is a plain attribute name until the user opens the field selector, at which point the
 * custom selector replaces it with the full field object — hence both shapes are accepted.
 */
export const isCompleteConditionRule = (rule: QueryBuilderRuleLike): boolean => {
  const hasField =
    typeof rule.field === 'string'
      ? Boolean(rule.field)
      : Boolean((rule.field as { value?: string } | undefined)?.value);

  const { value } = rule;
  const hasValue = Array.isArray(value)
    ? value.length > 0
    : value !== undefined && value !== null && String(value).trim() !== '';

  return hasField && hasValue;
};

/**
 * Drops rules the user never finished filling in, along with groups left empty as a result, so an
 * incomplete rule is not serialized with an `undefined` attribute.
 */
export const pruneIncompleteConditions = <
  T extends QueryBuilderRuleLike | QueryBuilderGroupLike,
>(
  rules: readonly T[] = [],
): T[] =>
  rules.reduce<T[]>((kept, rule) => {
    if (!isGroup(rule)) {
      return isCompleteConditionRule(rule) ? [...kept, rule] : kept;
    }

    const prunedRules = pruneIncompleteConditions(rule.rules);
    return prunedRules.length > 0 ? [...kept, { ...rule, rules: prunedRules }] : kept;
  }, []);

/**
 * Counts the rules that a user has actually filled in.
 *
 * A freshly added rule is pre-populated with the first available field and an empty value, so
 * `rules.length` alone does not tell us whether the user configured anything. Only rules with both
 * a picked field and a non-empty value are counted; nested groups are counted recursively.
 */
export const getValidConditionsCount = (group?: QueryBuilderGroupLike): number => {
  if (!group?.rules) {
    return 0;
  }

  return group.rules.reduce<number>((count, rule) => {
    if (isGroup(rule)) {
      return count + getValidConditionsCount(rule);
    }

    return isCompleteConditionRule(rule) ? count + 1 : count;
  }, 0);
};
